/**
 * Aba "Ordens de serviço" do ticket (spec 016): todas as OS do ticket (mais
 * de uma é normal — retorno, improdutiva), "Gerar OS" a partir da ficha na
 * descrição, ações de status, "Copiar para WhatsApp" e "Imprimir". Escrita só com
 * service_orders.manage (a API barra; o front esconde).
 */
import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Plus, Printer } from 'lucide-react';
import {
  isServiceOrderEditable,
  renderServiceOrderText,
  SERVICE_ORDER_PERIOD_LABELS,
  SERVICE_ORDER_STATUS_LABELS,
  SERVICE_ORDER_TRANSITIONS,
  SERVICE_ORDER_TYPE_LABELS,
  type ServiceOrder,
  type ServiceOrderDraft,
  type ServiceOrderStatus,
  type TicketDetail,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Label, Textarea } from '@/components/ui/input';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { useTransientToast } from '@/components/ui/transient-toast';
import { formatDateTime } from '@/lib/utils';
import { capitalize, useTerms } from '@/shared/tenant-profile';
import { ServiceOrderDialog } from './ServiceOrderDialog';
import {
  fetchServiceOrderDraft,
  formatIsoDate,
  SERVICE_ORDERS_KEY,
  serviceOrderErrorMessage,
  TECHNICIANS_KEY,
  ticketServiceOrdersQuery,
} from './service-orders-queries';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'destructive' | 'info';
export const STATUS_BADGE: Record<ServiceOrderStatus, BadgeVariant> = {
  draft: 'default',
  scheduled: 'info',
  done: 'success',
  unproductive: 'warning',
  canceled: 'destructive',
};

/** Rótulo do botão de cada transição, a partir do estado atual. */
const TRANSITION_LABELS: Partial<Record<ServiceOrderStatus, string>> = {
  scheduled: 'Agendar',
  done: 'Concluir',
  unproductive: 'Improdutiva',
  canceled: 'Cancelar OS',
};

type Target = Exclude<ServiceOrderStatus, 'draft'>;

export function ServiceOrdersTab({ ticket }: { ticket: TicketDetail }) {
  const ticketNumber = ticket.ticketNumber;
  const { can, ready } = usePermissions();
  const canManage = ready && can('service_orders.manage');
  const listQ = useQuery(ticketServiceOrdersQuery(ticketNumber));
  const [dialog, setDialog] = useState<ServiceOrderDraft | ServiceOrder | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ os: ServiceOrder; status: Target } | null>(null);
  const { toast, showToast } = useTransientToast('success');
  const terms = useTerms();

  const generate = useMutation({
    mutationFn: () => fetchServiceOrderDraft(ticketNumber),
    onSuccess: (draft) => setDialog(draft),
  });

  const copy = (os: ServiceOrder) =>
    void navigator.clipboard
      .writeText(renderServiceOrderText(os, terms))
      .then(() => showToast('Texto da OS copiado — cole no WhatsApp do técnico.'))
      .catch(() => showToast('Não foi possível copiar.'));

  const orders = listQ.data ?? [];

  return (
    <div className="space-y-4">
      <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Ordens de serviço</h2>
          {canManage ? (
            <Button size="sm" disabled={generate.isPending} onClick={() => generate.mutate()}>
              <Plus className="size-4" strokeWidth={2.5} />
              Gerar OS
            </Button>
          ) : null}
        </div>
        {generate.error ? (
          <p className="mb-2 text-sm text-destructive">{serviceOrderErrorMessage(generate.error)}</p>
        ) : null}

        {listQ.isPending ? (
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-24 w-full" />
          </div>
        ) : listQ.isError ? (
          <EmptyState message={`Erro ao carregar ordens de serviço: ${serviceOrderErrorMessage(listQ.error)}`} />
        ) : orders.length === 0 ? (
          <EmptyState className="h-28" message="Nenhuma ordem de serviço neste ticket" />
        ) : (
          <ul className="space-y-3">
            {orders.map((os) => (
              <ServiceOrderCard
                key={os.id}
                os={os}
                canManage={canManage}
                onEdit={() => setDialog(os)}
                onCopy={() => copy(os)}
                onStatus={(status) => setStatusTarget({ os, status })}
              />
            ))}
          </ul>
        )}
      </section>

      {dialog ? (
        <ServiceOrderDialog ticketNumber={ticketNumber} source={dialog} onClose={() => setDialog(null)} />
      ) : null}
      {statusTarget ? (
        <StatusDialog
          ticketNumber={ticketNumber}
          os={statusTarget.os}
          status={statusTarget.status}
          onClose={() => setStatusTarget(null)}
        />
      ) : null}
      {toast}
    </div>
  );
}

function ServiceOrderCard({
  os,
  canManage,
  onEdit,
  onCopy,
  onStatus,
}: {
  os: ServiceOrder;
  canManage: boolean;
  onEdit: () => void;
  onCopy: () => void;
  onStatus: (status: Target) => void;
}) {
  const terms = useTerms();
  const vehicle = [os.vehiclePlate, [os.vehicleBrand, os.vehicleModel].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ');
  const place = [os.addressNeighborhood, [os.addressCity, os.addressState].filter(Boolean).join('/')]
    .filter(Boolean)
    .join(' — ');
  const transitions = SERVICE_ORDER_TRANSITIONS[os.status].filter(
    (s): s is Target => s !== 'draft'
  );

  return (
    <li className="rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">OS nº {os.number}</span>
        <span className="text-sm text-muted-foreground">{SERVICE_ORDER_TYPE_LABELS[os.type]}</span>
        <Badge variant={STATUS_BADGE[os.status]}>{SERVICE_ORDER_STATUS_LABELS[os.status]}</Badge>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={onCopy} title="Copiar texto para o WhatsApp">
            <Copy />
            WhatsApp
          </Button>
          <Button asChild variant="ghost" size="sm" title="Versão para imprimir">
            <Link
              to="/tickets/$ticketNumber/service-orders/$id/print"
              params={{ ticketNumber: String(os.ticketNumber), id: String(os.id) }}
              target="_blank"
            >
              <Printer />
              Imprimir
            </Link>
          </Button>
          {canManage && isServiceOrderEditable(os.status) ? (
            <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
              <Pencil />
              Editar
            </Button>
          ) : null}
        </div>
      </div>
      <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <Info label="Data">
          {os.scheduledDate ? formatIsoDate(os.scheduledDate) : '—'}
          {os.scheduledPeriod ? ` — ${SERVICE_ORDER_PERIOD_LABELS[os.scheduledPeriod]}` : ''}
        </Info>
        <Info label="Técnico">{os.technician?.name ?? '—'}</Info>
        <Info label={capitalize(terms.resource)}>{vehicle || '—'}</Info>
        <Info label="Local">{place || '—'}</Info>
        {os.resultNote ? <Info label="Observação">{os.resultNote}</Info> : null}
        <Info label="Criada">
          {formatDateTime(os.createdAt)}
          {os.createdBy ? ` por ${os.createdBy.name}` : ''}
        </Info>
      </dl>
      {canManage && transitions.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2 border-t pt-3">
          {transitions.map((status) => (
            <Button
              key={status}
              type="button"
              size="sm"
              variant={status === 'canceled' ? 'outline' : status === 'unproductive' ? 'outline' : 'default'}
              onClick={() => onStatus(status)}
            >
              {TRANSITION_LABELS[status]}
            </Button>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

function StatusDialog({
  ticketNumber,
  os,
  status,
  onClose,
}: {
  ticketNumber: number;
  os: ServiceOrder;
  status: Target;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const noteRequired = status === 'unproductive' || status === 'canceled';

  const change = useMutation({
    mutationFn: async () => {
      const res = await api<ServiceOrder>(`/tickets/${ticketNumber}/service-orders/${os.id}/status`, {
        method: 'POST',
        body: { status, note },
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SERVICE_ORDERS_KEY });
      // improdutiva/cancelada liberam a vaga do dia
      void qc.invalidateQueries({ queryKey: TECHNICIANS_KEY });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (noteRequired && !note.trim()) return;
    change.mutate();
  };

  const title =
    status === 'scheduled'
      ? `Agendar OS nº ${os.number}`
      : status === 'done'
        ? `Concluir OS nº ${os.number}`
        : status === 'unproductive'
          ? `Marcar OS nº ${os.number} como improdutiva`
          : `Cancelar OS nº ${os.number}`;

  return (
    <Dialog open onClose={onClose} title={title}>
      <form onSubmit={submit} className="space-y-4">
        {status === 'scheduled' ? (
          <p className="text-sm text-muted-foreground">
            {os.technician && os.scheduledDate
              ? `Técnico ${os.technician.name}, ${formatIsoDate(os.scheduledDate)}.`
              : 'A OS precisa de técnico e data — edite antes de agendar.'}
          </p>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="os-status-note">
              {noteRequired ? 'Motivo' : 'Observação (opcional)'}
              {noteRequired ? <span className="text-destructive"> *</span> : null}
            </Label>
            <Textarea
              id="os-status-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoFocus
              required={noteRequired}
            />
          </div>
        )}
        {change.error ? (
          <p className="text-sm text-destructive">{serviceOrderErrorMessage(change.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" size="sm" disabled={change.isPending || (noteRequired && !note.trim())}>
            {change.isPending ? 'Salvando…' : 'Confirmar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
